import { type ChangeEvent, memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type IColor, useColor } from 'react-color-palette';

import { ColorPalettePicker } from '@air/component-color-palette-picker';
import { XIcon as CloseIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { hexToRgb, rgbToHsv } from '@air/utils-color';
import { reportErrorToBugsnag } from '@air/utils-error';

import { sanitizeColorValues } from './utils';

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}){1,2}$/;

/** `#abc` shorthand is the shortest form worth validating as the user types. */
const MIN_SHORTHAND_HEX_LENGTH = 3;
const HIDDEN_INPUT_TYPES: (keyof IColor)[] = ['rgb', 'hsv', 'hex'];

/** Empty or a leftover `#` after the user has backspaced the hex field. */
const isEmptyHexInput = (hex: string) => hex === '' || hex === '#';

/** Compare hexes without `#` or case so a parent echo cannot look like a new color. */
const canonicalizeHex = (hex: string) => hex.replace(/^#/, '').toLowerCase();

/**
 * `#abc` expands to `#aabbcc`. The committed color (preview + Save) is always 6-digit; the hex
 * field keeps what the user typed so a parent echo of the expansion cannot clobber backspace.
 */
const expandShorthandHex = (hex: string) => hex.replace(/^#([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/, '#$1$1$2$2$3$3');

export type CustomColorPickerProps = {
  defaultColor?: string;
  inputId?: string;
  inputLabel: string;
  onChange?: (color: string) => void;
  /**
   * When provided, the X (and Save on an emptied hex) commit a parent-owned clear instead of
   * restoring the field to the hex the picker opened with.
   */
  onClear?: () => void;
  onSave: (color: string) => void;
};

export const CustomColorPickerInput = memo((props: CustomColorPickerProps) => {
  const { defaultColor = '#FFFFFF', inputId, inputLabel, onChange, onClear, onSave } = props;
  const generatedInputId = useId();
  const resolvedInputId = inputId ?? generatedInputId;
  /**
   * `useColor` re-converts whenever its initial-color argument changes. Live preview updates the
   * parent's hex, which used to flow back in as `defaultColor` and expand a 3-digit shorthand
   * (clobbering backspace). Keep the opened hex stable unless the parent sends a color we did not
   * just emit (undo, an external reset) — those must replace the field.
   */
  const initialHexRef = useRef(defaultColor);
  const lastEmittedHexRef = useRef(canonicalizeHex(defaultColor));
  const hasSyncedDefaultColorRef = useRef<boolean>();
  // Parents often pass an inline onChange; a latest-only ref keeps the color effect off that identity.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [color, setColor] = useColor(initialHexRef.current);
  const [touched, setTouched] = useState(false);
  const [hexInput, setHexInput] = useState(color.hex);

  const showClearButton = onClear ? true : touched && hexInput !== initialHexRef.current;

  const handleHexChange = useCallback(
    (hex: string) => {
      const newHex = hex.replace(/[^0-9a-fA-F#]/g, ''); // Remove invalid characters
      setHexInput(newHex);

      // Only try to update color if we have a complete hex code
      if (newHex.length >= MIN_SHORTHAND_HEX_LENGTH) {
        const hexWithHash = newHex.startsWith('#') ? newHex : `#${newHex}`;

        // Check if it's a valid hex color (3 or 6 digits)
        if (HEX_COLOR_REGEX.test(hexWithHash)) {
          try {
            const rgb = hexToRgb(hexWithHash);

            if (rgb) {
              const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
              setColor({
                hex: expandShorthandHex(hexWithHash),
                hsv: { a: 1, h: hsv.h, s: hsv.s, v: hsv.v },
                rgb: { a: 1, b: rgb.b, g: rgb.g, r: rgb.r },
              });
            }
          } catch (error) {
            reportErrorToBugsnag({
              context: 'Failed to set color from hex input',
              error,
              metadata: {
                hexInput: newHex,
                hexWithHash,
              },
            });
          }
        }
      }
    },
    [setColor],
  );

  const handleSave = useCallback(() => {
    if (isEmptyHexInput(hexInput) && onClear) {
      onClear();
      return;
    }

    onSave(color.hex);
  }, [color.hex, hexInput, onClear, onSave]);

  const handleColorPalettePickerChange = useCallback(
    (nextColor: IColor) => {
      const sanitized = sanitizeColorValues(nextColor);
      setColor(sanitized);
      setHexInput(sanitized.hex);
      setTouched(true);
    },
    [setColor],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTouched(true);
      handleHexChange(event.target.value);
    },
    [handleHexChange],
  );

  const handleClear = useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }

    setTouched(false);
    handleHexChange(initialHexRef.current);
  }, [handleHexChange, onClear]);

  // Preview the last committed picker color. Hex typing owns `hexInput` itself — syncing it from
  // `color.hex` here used to expand a 3-digit shorthand and undo backspace.
  useEffect(() => {
    lastEmittedHexRef.current = canonicalizeHex(color.hex);
    onChangeRef.current?.(color.hex);
  }, [color]);

  // Apply parent-owned colors (canvas undo, an external reset) but ignore the live-preview echo
  // of what we just emitted — that loop is what used to clobber backspace.
  useEffect(() => {
    if (!hasSyncedDefaultColorRef.current) {
      hasSyncedDefaultColorRef.current = true;
      return;
    }

    if (canonicalizeHex(defaultColor) === lastEmittedHexRef.current) {
      return;
    }

    initialHexRef.current = defaultColor;
    setTouched(false);
    handleHexChange(defaultColor);
  }, [defaultColor, handleHexChange]);

  // Check if the current hex input is valid
  const isValidHex = useMemo(() => {
    if (isEmptyHexInput(hexInput)) {
      return false;
    }
    const hexWithHash = hexInput.startsWith('#') ? hexInput : `#${hexInput}`;

    return HEX_COLOR_REGEX.test(hexWithHash);
  }, [hexInput]);

  const canSave = isValidHex || (!!onClear && isEmptyHexInput(hexInput));

  return (
    <div className="color-picker-container flex flex-col gap-2">
      <div className="flex w-full items-center gap-2">
        <div className="relative w-full min-w-0 flex-1">
          <Label className="sr-only" data-testid="CUSTOM_COLOR_HEX_INPUT_LABEL" htmlFor={resolvedInputId}>
            {inputLabel}
          </Label>
          <Input
            className={showClearButton ? 'w-full pr-8 uppercase' : 'w-full uppercase'}
            data-testid="CUSTOM_COLOR_HEX_INPUT"
            id={resolvedInputId}
            onChange={handleInputChange}
            placeholder="#000000"
            type="text"
            value={hexInput}
          />
          {showClearButton && (
            <button
              aria-label="Clear custom color"
              className="absolute top-1/2 right-2 -translate-y-1/2 p-1"
              data-testid="CUSTOM_COLOR_CLEAR_BUTTON"
              onClick={handleClear}
              type="button"
            >
              <CloseIcon size="xs" />
            </button>
          )}
        </div>
        <Button
          appearance="filled"
          color="gray"
          data-testid="CUSTOM_COLOR_SAVE_BUTTON"
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
      <ColorPalettePicker
        color={color}
        data-testid="COLOR_PICKER"
        hideAlpha
        hideInput={HIDDEN_INPUT_TYPES}
        onChange={handleColorPalettePickerChange}
      />
    </div>
  );
});

CustomColorPickerInput.displayName = 'CustomColorPickerInput';
